import React, { useEffect, useRef } from 'react';
import { FiDownload, FiExternalLink } from 'react-icons/fi';

function SudokuGame() {
  const iframeRef = useRef(null);

  useEffect(() => {
    console.log('Loading sudoku emulator...');
    if (iframeRef.current) {
      iframeRef.current.src = '/sudoku-emulator.html';
    }
  }, []);

  return (
    <div className="game-detail-container">
      {/* Blueprint Grid Background */}
      <div className="game-grid-overlay" aria-hidden="true" />

      {/* Main Content */}
      <div className="game-content">
        {/* Header */}
        <div className="game-header">
          <div className="game-badge font-mono">
            <span className="game-dot" />
            PLAYABLE BROWSER EMULATOR &nbsp;•&nbsp; X86 ASSEMBLY
          </div>
          <h1 className="game-title font-display">
            Sudoku Game <span className="game-title-accent">Engine</span>
          </h1>
          <p className="game-subtitle font-mono">
            Compiled from x86 Assembly source code &amp; executed via JS-DOS emulator
          </p>
        </div>

        {/* Game Player Section */}
        <div className="game-section">
          <div className="game-frame-card">
            <div className="game-frame-topbar font-mono">
              <span className="topbar-dot red" />
              <span className="topbar-dot yellow" />
              <span className="topbar-dot green" />
              <span className="topbar-title">SUDOKU.EXE — DOS EMULATOR</span>
            </div>
            <div className="game-frame-body">
              <iframe
                ref={iframeRef}
                className="dos-iframe"
                title="DOS Emulator"
              />
            </div>
          </div>

          {/* Quick Specs Cards */}
          <div className="info-cards">
            <div className="info-card">
              <div className="info-icon font-mono">06</div>
              <div className="info-content">
                <span className="info-label font-mono">DIFFICULTY LEVELS</span>
                <span className="info-value font-heading">Beginner to Master</span>
              </div>
            </div>
            <div className="info-card">
              <div className="info-icon font-mono">⏱</div>
              <div className="info-content">
                <span className="info-label font-mono">REALTIME TIMER</span>
                <span className="info-value font-heading">Integrated Counter</span>
              </div>
            </div>
            <div className="info-card">
              <div className="info-icon font-mono">📝</div>
              <div className="info-content">
                <span className="info-label font-mono">NOTES MODE</span>
                <span className="info-value font-heading">Pencil Candidates</span>
              </div>
            </div>
            <div className="info-card">
              <div className="info-icon font-mono">⟲</div>
              <div className="info-content">
                <span className="info-label font-mono">UNDO STACK</span>
                <span className="info-value font-heading">Unlimited History</span>
              </div>
            </div>
          </div>
        </div>

        {/* Features & Controls Grid */}
        <div className="features-grid">
          {/* Features Column */}
          <div className="features-column">
            <h3 className="column-title font-heading">
              <span className="column-dot gold-dot" /> Features &amp; Architecture
            </h3>
            <div className="features-list">
              {[
                '6 Difficulty Levels',
                'Real-time Timer & Scoring',
                'Advanced Candidate Notes',
                'Unlimited Undo History',
                'Instant Rule Validation',
                'Trophy & Victory Screen',
                'Mistake Counter & Penalty',
                'Clean ASCII Art UI'
              ].map((feature, index) => (
                <div key={index} className="feature-item font-mono">
                  <span className="feature-bullet">▸</span>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Controls Column */}
          <div className="controls-column">
            <h3 className="column-title font-heading">
              <span className="column-dot cyan-dot" /> Keyboard Controls
            </h3>
            <div className="controls-list">
              {[
                { keys: '↑ ↓ ← →', desc: 'Navigate grid cells' },
                { keys: '1 – 9', desc: 'Place number candidate' },
                { keys: 'U', desc: 'Undo last move' },
                { keys: 'N', desc: 'Toggle pencil notes mode' },
                { keys: 'Space', desc: 'Clear active cell' },
                { keys: 'ESC', desc: 'Return to DOS main menu' }
              ].map((control, index) => (
                <div key={index} className="control-item">
                  <span className="control-keys font-mono">{control.keys}</span>
                  <span className="control-desc font-mono">{control.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* About & Source Download Section */}
        <div className="about-section">
          <h3 className="section-title font-heading">
            <span className="column-dot gold-dot" /> Low-Level Systems Spec
          </h3>
          
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-detail font-mono">
                <span className="stat-label">LANGUAGE</span>
                <span className="stat-value">x86 Assembly (16-bit)</span>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-detail font-mono">
                <span className="stat-label">LINES OF CODE</span>
                <span className="stat-value">~2,748 Assembly instructions</span>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-detail font-mono">
                <span className="stat-label">PLATFORM</span>
                <span className="stat-value">MS-DOS / JS-DOS 7.0</span>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-detail font-mono">
                <span className="stat-label">ALGORITHM</span>
                <span className="stat-value">Recursive Backtracking Solver</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <a
              href="/suduko.asm"
              className="btn-asm-download font-mono"
              download="suduko.asm"
            >
              <FiDownload className="btn-icon" />
              <span>Download Assembly Source (.asm)</span>
            </a>
            
            <div className="resource-links font-mono">
              <span className="resource-label">REFERENCES:</span>
              <a href="https://js-dos.com/" target="_blank" rel="noopener noreferrer" className="resource-link">
                js-dos engine <FiExternalLink className="link-icon" />
              </a>
              <a href="https://www.nasm.us/" target="_blank" rel="noopener noreferrer" className="resource-link">
                NASM <FiExternalLink className="link-icon" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .game-detail-container {
          width: 100%;
          position: relative;
          background: #080a0f;
          color: #f3f4f6;
          padding: 3rem 1.5rem;
        }

        .game-grid-overlay {
          position: absolute;
          inset: 0;
          background-image: 
            linear-gradient(rgba(212, 163, 89, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(212, 163, 89, 0.03) 1px, transparent 1px);
          background-size: 50px 50px;
          pointer-events: none;
        }

        .game-content {
          position: relative;
          z-index: 10;
          max-width: 960px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 2.5rem;
        }

        .game-header {
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
        }

        .game-badge {
          font-size: 0.72rem;
          letter-spacing: 0.2em;
          color: #d4a359;
          display: flex;
          align-items: center;
          gap: 0.6rem;
          background: rgba(212, 163, 89, 0.08);
          border: 1px solid rgba(212, 163, 89, 0.25);
          padding: 0.35rem 0.9rem;
          border-radius: 100px;
        }

        .game-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #d4a359;
          box-shadow: 0 0 8px #d4a359;
        }

        .game-title {
          font-size: clamp(2.2rem, 5vw, 3.5rem);
          font-weight: 800;
          color: #ffffff;
          margin: 0;
          line-height: 1.15;
          letter-spacing: -0.03em;
        }

        .game-title-accent {
          color: #d4a359;
          font-style: italic;
          font-family: 'Playfair Display', Georgia, serif;
          font-weight: 500;
        }

        .game-subtitle {
          font-size: 0.8rem;
          letter-spacing: 0.1em;
          color: rgba(156, 163, 175, 0.8);
          margin: 0;
        }

        /* Game Section */
        .game-section {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .game-frame-card {
          background: rgba(14, 18, 27, 0.95);
          border: 1px solid rgba(212, 163, 89, 0.25);
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.6);
        }

        .game-frame-topbar {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.25rem;
          background: rgba(8, 10, 15, 0.9);
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
          font-size: 0.7rem;
          letter-spacing: 0.12em;
          color: rgba(156, 163, 175, 0.8);
        }

        .topbar-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
        }

        .topbar-dot.red { background: #f43f5e; }
        .topbar-dot.yellow { background: #d4a359; }
        .topbar-dot.green { background: #10b981; }

        .topbar-title {
          margin-left: 0.5rem;
        }

        .game-frame-body {
          width: 100%;
          height: 480px;
          background: #000;
        }

        .dos-iframe {
          width: 100%;
          height: 100%;
          border: none;
          display: block;
        }

        /* Info Cards */
        .info-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .info-card {
          background: rgba(16, 21, 31, 0.75);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 10px;
          padding: 1.1rem 1.25rem;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .info-icon {
          font-size: 1.1rem;
          color: #d4a359;
          font-weight: 700;
        }

        .info-content {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
        }

        .info-label {
          font-size: 0.62rem;
          letter-spacing: 0.14em;
          color: rgba(156, 163, 175, 0.7);
        }

        .info-value {
          font-size: 0.95rem;
          font-weight: 600;
          color: #ffffff;
        }

        /* Features & Controls */
        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 1.5rem;
        }

        .features-column,
        .controls-column,
        .about-section {
          background: rgba(16, 21, 31, 0.75);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 14px;
          padding: 1.75rem;
        }

        .column-title,
        .section-title {
          font-size: 1.1rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 1.25rem;
          display: flex;
          align-items: center;
          gap: 0.6rem;
        }

        .column-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
        }

        .gold-dot { background: #d4a359; box-shadow: 0 0 8px #d4a359; }
        .cyan-dot { background: #38bdf8; box-shadow: 0 0 8px #38bdf8; }

        .features-list {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: rgba(209, 213, 219, 0.85);
          font-size: 0.78rem;
        }

        .feature-bullet {
          color: #d4a359;
          font-size: 0.7rem;
        }

        .controls-list {
          display: flex;
          flex-direction: column;
          gap: 0.65rem;
        }

        .control-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.6rem 0.85rem;
          background: rgba(8, 10, 15, 0.6);
          border-radius: 6px;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .control-keys {
          background: rgba(212, 163, 89, 0.1);
          border: 1px solid rgba(212, 163, 89, 0.25);
          padding: 0.25rem 0.6rem;
          border-radius: 4px;
          font-size: 0.72rem;
          color: #d4a359;
          min-width: 90px;
          text-align: center;
        }

        .control-desc {
          color: rgba(209, 213, 219, 0.8);
          font-size: 0.78rem;
        }

        /* About Specs */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 1.75rem;
        }

        .stat-item {
          padding: 0.85rem 1rem;
          background: rgba(8, 10, 15, 0.6);
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .stat-detail {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
        }

        .stat-label {
          font-size: 0.62rem;
          letter-spacing: 0.14em;
          color: rgba(156, 163, 175, 0.7);
        }

        .stat-value {
          font-size: 0.82rem;
          color: #ffffff;
        }

        /* Action Buttons */
        .action-buttons {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.25rem;
          padding-top: 1rem;
          border-top: 1px solid rgba(255, 255, 255, 0.06);
        }

        .btn-asm-download {
          background: #d4a359;
          color: #080a0f;
          padding: 0.75rem 1.6rem;
          border-radius: 8px;
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
          transition: all 0.2s ease;
          box-shadow: 0 4px 20px rgba(212, 163, 89, 0.25);
        }

        .btn-asm-download:hover {
          background: #e6b46a;
          box-shadow: 0 6px 28px rgba(212, 163, 89, 0.4);
          transform: translateY(-1px);
        }

        .resource-links {
          display: flex;
          align-items: center;
          gap: 1rem;
          font-size: 0.72rem;
          color: rgba(156, 163, 175, 0.7);
        }

        .resource-link {
          color: #38bdf8;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
        }

        .resource-link:hover {
          color: #60a5fa;
          text-decoration: underline;
        }

        @media (max-width: 640px) {
          .game-frame-body {
            height: 340px;
          }
          .features-list {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}

export default SudokuGame;